import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, Clock } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Tiny ops pulse — only renders when ?token=<ADMIN_TOKEN> matches the backend.
 * No bells, no auth UI. Bookmark the URL with the token to monitor leads.
 */
export default function AdminPulse() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) {
      setErr("Missing token.");
      return;
    }
    const tick = () =>
      fetch(`${API}/callbacks/pulse?token=${encodeURIComponent(token)}`)
        .then((r) =>
          r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))
        )
        .then(setData)
        .catch((e) => setErr(e.message));
    tick();
    const id = setInterval(tick, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [token]);

  return (
    <div className="bg-[#F9FAFB] py-16" data-testid="admin-pulse-page">
      <div className="mx-auto max-w-md px-5">
        <Card className="border-[#0A1128]/10 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              <Activity size={12} /> WillSeva pulse
            </div>
            {err && (
              <p className="mt-3 text-sm text-red-700" data-testid="admin-pulse-error">
                {err}
              </p>
            )}
            {!err && !data && (
              <p className="mt-3 text-sm text-[#4A5568]">Loading…</p>
            )}
            {data && (
              <>
                <div className="mt-3 font-serif text-3xl text-navy">
                  {data.count}
                  <span className="ml-2 text-sm text-[#4A5568]">total leads</span>
                </div>
                <div
                  className="mt-4 rounded-md border border-[#0A1128]/10 bg-[#FBF7EE] p-3 text-sm text-navy"
                  data-testid="admin-pulse-last"
                >
                  <div className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[#4A5568]">
                    <Clock size={11} /> Last lead
                  </div>
                  <div className="mt-1.5 font-medium">
                    {data.last_name || "—"}
                  </div>
                  <div className="text-xs text-[#4A5568]">
                    {data.last_phone || "—"}
                    {data.last_plan ? ` · ${data.last_plan}` : ""}
                    {data.last_source ? ` · ${data.last_source}` : ""}
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-[#4A5568]">
                    {data.last_at || "—"}
                  </div>
                </div>
              </>
            )}
            <p className="mt-4 text-[11px] text-[#9CA3AF]">Refreshes every 30s.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
